import React, { useState } from 'react';
import { 
  ComposedChart, 
  Line, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend 
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { weatherData, provinces } from '../data/mockData';

const WeatherChart = () => {
  const [selectedProvince, setSelectedProvince] = useState('ON');
  
  const prepareChartData = () => {
    const tempData = weatherData[selectedProvince].temperature;
    const precipData = weatherData[selectedProvince].precipitation;
    
    return tempData.map((temp, index) => ({
      year: temp.year,
      avgTemp: temp.avg,
      minTemp: temp.min,
      maxTemp: temp.max,
      totalPrecip: precipData[index].total,
      growingPrecip: precipData[index].growing
    }));
  };

  const getProvinceName = (id) => {
    const province = provinces.find(p => p.id === id);
    return province ? province.name : id;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded shadow">
          <p className="font-medium">{`Year: ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.name}: ${entry.value}${entry.name.includes('Temp') ? '°C' : 'mm'}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Weather Trends - {getProvinceName(selectedProvince)}</span>
          <Select value={selectedProvince} onValueChange={setSelectedProvince}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {provinces.map(province => (
                <SelectItem key={province.id} value={province.id}>
                  {province.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={prepareChartData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis yAxisId="temp" orientation="left" />
              <YAxis yAxisId="precip" orientation="right" />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                yAxisId="precip" 
                dataKey="totalPrecip" 
                fill="#8884d8" 
                name="Total Precipitation" 
                opacity={0.6}
              />
              <Bar 
                yAxisId="precip" 
                dataKey="growingPrecip" 
                fill="#82ca9d" 
                name="Growing Season Precipitation" 
                opacity={0.8}
              />
              <Line 
                yAxisId="temp" 
                type="monotone" 
                dataKey="avgTemp" 
                stroke="#ffc658" 
                strokeWidth={3}
                name="Average Temperature"
                dot={{ fill: '#ffc658', strokeWidth: 2, r: 4 }}
              />
              <Line 
                yAxisId="temp" 
                type="monotone" 
                dataKey="maxTemp" 
                stroke="#ff7300" 
                strokeWidth={2}
                strokeDasharray="5 5"
                name="Max Temperature"
                dot={{ fill: '#ff7300', strokeWidth: 2, r: 3 }}
              />
              <Line 
                yAxisId="temp" 
                type="monotone" 
                dataKey="minTemp" 
                stroke="#387908" 
                strokeWidth={2}
                strokeDasharray="5 5"
                name="Min Temperature"
                dot={{ fill: '#387908', strokeWidth: 2, r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default WeatherChart;